import { useState, useEffect } from 'react';
import { X, Save, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAdminOverlayStore } from '../../stores/admin-overlay-store';
import { useWebsiteContent } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';

export default function InlineEditModal() {
  const { editingElement, closeEditor } = useAdminOverlayStore();
  const { updateContent, updateImage, getImage } = useWebsiteContent();
  const { t } = useLanguage();

  const [value, setValue] = useState('');
  const [altText, setAltText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize values when editing element changes
  useEffect(() => {
    if (editingElement) {
      setValue(editingElement.currentValue);
      setError('');

      // For images, also get alt text
      if (editingElement.type === 'image') {
        const img = getImage(`${editingElement.section}.${editingElement.key}`);
        setAltText(img?.alt_text ?? '');
      }
    }
  }, [editingElement, getImage]);

  if (!editingElement) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      if (editingElement.type === 'text') {
        await updateContent(editingElement.section, editingElement.key, value);
      } else if (editingElement.type === 'image') {
        await updateImage(
          `${editingElement.section}.${editingElement.key}`,
          value,
          altText
        );
      }
      closeEditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeEditor();
    } else if (e.key === 'Enter' && e.metaKey) {
      handleSave();
    }
  };

  const formatFieldName = (section: string, key: string) => {
    const sectionName = section.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const keyName = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    return `${sectionName} - ${keyName}`;
  };

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={closeEditor}
        onKeyDown={(e) => e.key === 'Escape' && closeEditor()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg"
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingElement.type === 'image' ? t('inlineEditModal.editImage', 'Edit Image') : t('inlineEditModal.editContent', 'Edit Content')}
            </h2>
            <button
              onClick={closeEditor}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Field name */}
            <div className="text-sm text-gray-500">
              {formatFieldName(editingElement.section, editingElement.key)}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Text editing */}
            {editingElement.type === 'text' && (
              <div>
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  placeholder={t('inlineEditModal.enterContent', 'Enter content...')}
                  autoFocus
                />
                <div className="mt-1 text-xs text-gray-400 text-right">
                  {t('inlineEditModal.characterCount', '{count} characters').replace('{count}', String(value.length))}
                </div>
              </div>
            )}

            {/* Image editing */}
            {editingElement.type === 'image' && (
              <div className="space-y-4">
                {/* Image preview */}
                {value && (
                  <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={value}
                      alt={altText || 'Preview'}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {!value && (
                  <div className="flex items-center justify-center aspect-video bg-gray-100 rounded-lg">
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  </div>
                )}

                {/* URL input */}
                <div>
                  <label htmlFor="image-url" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('inlineEditModal.imageUrl', 'Image URL')}
                  </label>
                  <input
                    id="image-url"
                    type="url"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                    autoFocus
                  />
                </div>

                {/* Alt text input */}
                <div>
                  <label htmlFor="alt-text" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('inlineEditModal.altText', 'Alt Text')}
                  </label>
                  <input
                    id="alt-text"
                    type="text"
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('inlineEditModal.describeImage', 'Describe the image...')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={closeEditor}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              disabled={isSaving}
            >
              {t('inlineEditModal.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('inlineEditModal.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {t('inlineEditModal.save', 'Save')}
                </>
              )}
            </button>
          </div>

          {/* Keyboard shortcut hint */}
          <div className="px-6 pb-3 text-xs text-gray-400 text-center">
            Press <kbd className="px-1 py-0.5 bg-gray-100 rounded">Esc</kbd> to cancel,{' '}
            <kbd className="px-1 py-0.5 bg-gray-100 rounded">Cmd+Enter</kbd> to save
          </div>
        </div>
      </div>
    </div>
  );
}
